import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';

// Authentication Pages
import LoginPage from './pages/auth/login';

// Dashboard and Admin Pages
import AdminDashboard from './pages/admin/index';
import PrivateRoute from './routes/private-route';

// General Pages
import Welcome from './pages/welcome/welcome';
import NotFound404 from './pages/404';
import NoAccess from './pages/no-access';

// Application Pages
import Articles from './pages/articles';
import ProductDetails from './pages/articles/product-details';
import AddProduct from './pages/articles/add-product';
import Commandes from './pages/commandes';
import OrderDetails from './pages/commandes/order-details';
import Customers from './pages/customers';
import CustomerInvoices from './pages/customers/invoices';
import Personnel from './pages/personnel';
import DossierPersonnel from './pages/personnel/dossier';
import PaieMois from './pages/personnel/paie';
import BulletinPaie from './pages/personnel/bulletin';
import ReglagesPaie from './pages/personnel/reglages';
import Suppliers from './pages/suppliers';
import SupplierArticles from './pages/suppliers/articles';
import Stores from './pages/stores';
import Rapports from './pages/rapports';
import Category from './pages/categories';
import SubCategories from './pages/sub-categories';
import Units from './pages/units';
import Warehouses from './pages/warehouses';
import Validations from './pages/validations';
import Supply from './pages/supply';
import AddSupply from './pages/supply/add-supply';
import UpdateSupply from './pages/supply/update-supply';
import Brands from './pages/admin/brands';
import GuestProfile from './pages/profile/guest-profile';
import RolesPermissions from './pages/roles-permissions';
import UpdateInvoice from './pages/commandes/update-invoice';
import OrderValidation from './pages/commandes/validate';
import { useSelector } from 'react-redux';
import History from './pages/commandes/history';
import CaissierValidation from './pages/caissier';
import CaissierUpdateInvoice from './pages/caissier/update-invoice';
import CaissierHistory from './pages/caissier/history';
import MagasinierValidation from './pages/magasinier';
import MagasinierUpdateInvoice from './pages/magasinier/update-invoice';
import MagasinierHistory from './pages/magasinier/history';
import MagasinierSummary from './pages/magasinier/summary';
import EditProfile from './pages/magasinier/edit-profile';
import MagasinierEditProfile from './pages/magasinier/edit-profile';
import CaissierEditProfile from './pages/caissier/edit-profile';
import Comptable from './pages/comptable';
import ComptableEditProfile from './pages/comptable/edit-profile';
import Formats from './pages/formats';
import AdminOrdersList from './pages/admin/orders';
import AdminUpdateInvoice from './pages/commandes/admin-update-invoice';
import ComptableUpdateInvoice from './pages/comptable/invoice';
import ProFormatInvoice from './pages/commandes/pro-format-invoice';
import AdminProFormatInvoice from './pages/admin/pro-format-invoice';
import IsActif from './pages/IsActif';
import AdminSummary from './pages/admin/summary';
import StockAdjustments from './pages/stock-adjustments';
import InstallmentOrder from './pages/installment-order';
import InstallmentInvoice from './pages/installment-order/invoice';
import InstallmentOrderVendeur from './pages/installment-order/vendeur';
import AddBrokenTile from './pages/admin/broken-tiles/create';
import BrokenTilesList from './pages/admin/broken-tiles';
import SalesBrokenTiles from './pages/admin/broken-tiles/sales';
import ControllerPage from './pages/controller';
import ControllerInvoice from './pages/controller/invoice';
import ControllerValidatedPage from './pages/controller/history';
import ControllerProfile from './pages/controller/profile';
import BrokenTileSalesList from './pages/admin/broken-tiles/list-sale';
import ReportsAdminIndex from './pages/rapports';
import FinanceImpotsPage from './pages/finance-impots';
// import ReportsUserIndex from './pages/personnel/reports';
import AdminStock from './pages/admin/stock';
import BestSelling from './pages/admin/best-selling';
// import AdminReports from './pages/admin/reports';
import ReportsAdmin from './pages/admin/reports-admin';
import ValidateBrokenTiles from './pages/admin/broken-tiles/validate';
import DamagedGoodsList from './pages/admin/damaged-goods';
import AddDamagedGood from './pages/admin/damaged-goods/create';
import SalesDamagedGoods from './pages/admin/damaged-goods/sales';
import DamagedGoodSalesList from './pages/admin/damaged-goods/list-sale';
import ValidateDamagedGood from './pages/admin/damaged-goods/validate';
import DonationsList from './pages/admin/donations';
import AddDonation from './pages/admin/donations/create';
import ComptableSalesPage from './pages/comptable/sales';
import ComptableInventoryPage from './pages/comptable/inventory';
import BalanceInvoice from './pages/customers/invoice';
import AccesPages from './pages/parametres/acces';
import { renderGrantedRoutes } from './routes/granted-routes';
import { pageList } from './utils/gestion-access';

const App = () => {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const user = useSelector((state) => state.auth.user);
  const userRole = user?.personnels?.[0]?.poste_id;
  const userStatus = user?.personnels?.[0]?.statut;
  const userAdmin = user?.role === 'Administrateur';
  const superAdmin = user?.role === 'superAdmin';

  return (
    <Routes>

      {/* Authentication Routes */}
      <Route path="/auth/login" element={<LoginPage />} />

      {/* Private Routes (Requires Authentication) */}
      <Route element={<PrivateRoute />}>
        {isAuthenticated && (
          <>
            {(userAdmin || userRole === 13) && (
              <>
                <Route path="/" element={<Welcome />} />
                <Route path="/articles" element={<Articles />} />
                <Route path="/product-details/:id" element={<ProductDetails />} />
                <Route path="/add-product" element={<AddProduct />} />
                {Number(userRole) !== 13 && (
                  <>
                    <Route path="/personnel" element={<Personnel />} />
                    <Route path="/personnel/paie" element={<PaieMois />} />
                    <Route path="/personnel/paie/reglages" element={<ReglagesPaie />} />
                    <Route path="/personnel/paie/:personnelId" element={<BulletinPaie />} />
                    <Route path="/personnel/:id/dossier" element={<DossierPersonnel />} />
                  </>
                )}
                {/* <Route path="/roles-permissions" element={<RolesPermissions />} /> */}
                <Route path="/commandes" element={<Commandes />} />
                <Route path="/clients" element={<Customers />} />
                <Route path="/clients/:customerId/factures" element={<CustomerInvoices />} />
                <Route path="/fournisseurs" element={<Suppliers />} />
                <Route path="/fournisseurs/articles" element={<SupplierArticles />} />
                <Route path="/users-profile" element={<GuestProfile />} />
                <Route patVentesh="/admin" element={<AdminDashboard />} />
                <Route path="/categories" element={<Category />} />
                <Route path="/sub-categories" element={<SubCategories />} />
                <Route path="/admin/brands" element={<Brands />} />
                <Route path="/units" element={<Units />} />
                <Route path="/formats" element={<Formats />} />
                <Route path="/warehouses" element={<Warehouses />} />
                <Route path="/stores" element={<Stores />} />
                <Route path="/validations" element={<Validations />} />
                <Route path="/order-details" element={<OrderDetails />} />
                <Route path="/approvisionnement" element={<Supply />} />
                <Route path="/add-supply" element={<AddSupply />} />
                <Route path="/update-supply/:itemId" element={<UpdateSupply />} />
                <Route path="/commande/:reference" element={<AdminUpdateInvoice />} />
                <Route path="/commandes/validation" element={<OrderValidation />} />
                <Route path="/edit-profile" element={<CaissierEditProfile />} />
                <Route path="/commandes/pros-format" element={<ProFormatInvoice />} />
                <Route path="/marques" element={<Brands />} />
                <Route path="/admin/ventes" element={<AdminOrdersList />} />
                <Route path="/admin/summary" element={<AdminSummary />} />
                <Route path="/admin/stock-adjustments" element={<StockAdjustments />} />
                <Route path="/admin/installment-orders" element={<InstallmentOrder />} />
                <Route path="/admin/installment-orders/:id" element={<InstallmentInvoice />} />

                <Route path="/admin/broken-tiles" element={<BrokenTilesList />} />
                <Route path="/admin/broken-tiles/create" element={<AddBrokenTile />} />
                <Route path="/stock-available" element={<AdminStock />} />
                <Route path="/admin/sales-broken-tiles" element={<SalesBrokenTiles />} />
                <Route path="/admin/sales-broken-tiles/list" element={<BrokenTileSalesList />} />
                <Route path="/broken-tiles/validate/:ref" element={<ValidateBrokenTiles />} />
                <Route path="/admin/damaged-goods" element={<DamagedGoodsList />} />
                <Route path="/admin/damaged-goods/create" element={<AddDamagedGood />} />
                <Route path="/admin/damaged-goods/sales" element={<SalesDamagedGoods />} />
                <Route path="/admin/damaged-goods/sales/list" element={<DamagedGoodSalesList />} />
                <Route path="/admin/damaged-goods/validate/:ref" element={<ValidateDamagedGood />} />
                <Route path="/admin/dons" element={<DonationsList />} />
                <Route path="/admin/dons/nouveau" element={<AddDonation />} />
                {/* <Route path="/reports" element={<AdminReports />} /> */}
                <Route path="/balance/invoice/:id" element={<BalanceInvoice />} />
                <Route path="/business-reports" element={<ReportsAdmin />} />

                <Route path="/inventory" element={<ComptableInventoryPage />} />

              </>
            )}

            {superAdmin && (
              <>
                <Route path="/" element={<Welcome />} />
                <Route path="/articles" element={<Articles />} />
                <Route path="/product-details/:id" element={<ProductDetails />} />
                <Route path="/add-product" element={<AddProduct />} />
                <Route path="/personnel" element={<Personnel />} />
                <Route path="/personnel/paie" element={<PaieMois />} />
                <Route path="/personnel/paie/reglages" element={<ReglagesPaie />} />
                <Route path="/personnel/paie/:personnelId" element={<BulletinPaie />} />
                <Route path="/personnel/:id/dossier" element={<DossierPersonnel />} />
                <Route path="/roles-permissions" element={<RolesPermissions />} />
                <Route path="/parametres/acces" element={<AccesPages />} />
                <Route path="/livraisons" element={<MagasinierValidation />} />
                <Route path="/commandes/history" element={<MagasinierHistory />} />
                <Route path="/summary" element={<MagasinierSummary />} />
                <Route path="/comptable" element={<Comptable />} />
                <Route path="/inventory" element={<ComptableInventoryPage />} />
                <Route path="/sales" element={<ComptableSalesPage />} />
                <Route path="/commandes" element={<Commandes />} />
                <Route path="/clients" element={<Customers />} />
                <Route path="/clients/:customerId/factures" element={<CustomerInvoices />} />
                <Route path="/fournisseurs" element={<Suppliers />} />
                <Route path="/fournisseurs/articles" element={<SupplierArticles />} />
                <Route path="/users-profile" element={<GuestProfile />} />
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/categories" element={<Category />} />
                <Route path="/sub-categories" element={<SubCategories />} />
                <Route path="/admin/brands" element={<Brands />} />
                <Route path="/units" element={<Units />} />
                <Route path="/formats" element={<Formats />} />
                <Route path="/warehouses" element={<Warehouses />} />
                <Route path="/stores" element={<Stores />} />
                <Route path="/validations" element={<Validations />} />
                <Route path="/order-details" element={<OrderDetails />} />
                <Route path="/approvisionnement" element={<Supply />} />
                <Route path="/add-supply" element={<AddSupply />} />
                <Route path="/update-supply/:itemId" element={<UpdateSupply />} />
                <Route path="/commande/:reference" element={<AdminUpdateInvoice />} />
                <Route path="/commandes/validation" element={<OrderValidation />} />
                <Route path="/edit-profile" element={<CaissierEditProfile />} />
                <Route path="/commandes/pros-format" element={<ProFormatInvoice />} />
                <Route path="/marques" element={<Brands />} />
                <Route path="/admin/ventes" element={<AdminOrdersList />} />
                <Route path="/admin/summary" element={<AdminSummary />} />
                <Route path="/admin/stock-adjustments" element={<StockAdjustments />} />
                <Route path="/admin/installment-orders" element={<InstallmentOrder />} />
                <Route path="/admin/installment-orders/:id" element={<InstallmentInvoice />} />

                <Route path="/admin/broken-tiles" element={<BrokenTilesList />} />
                <Route path="/admin/broken-tiles/create" element={<AddBrokenTile />} />
                <Route path="/admin/sales-broken-tiles" element={<SalesBrokenTiles />} />
                <Route path="/admin/sales-broken-tiles/list" element={<BrokenTileSalesList />} />
                <Route path="/broken-tiles/validate/:ref" element={<ValidateBrokenTiles />} />
                <Route path="/admin/damaged-goods" element={<DamagedGoodsList />} />
                <Route path="/admin/damaged-goods/create" element={<AddDamagedGood />} />
                <Route path="/admin/damaged-goods/sales" element={<SalesDamagedGoods />} />
                <Route path="/admin/damaged-goods/sales/list" element={<DamagedGoodSalesList />} />
                <Route path="/admin/damaged-goods/validate/:ref" element={<ValidateDamagedGood />} />
                <Route path="/admin/dons" element={<DonationsList />} />
                <Route path="/admin/dons/nouveau" element={<AddDonation />} />
                <Route path="/stock-available" element={<AdminStock />} />
                <Route path="/best-selling" element={<BestSelling />} />
                {/* <Route path="/reports" element={<AdminReports />} /> */}
                <Route path="/business-reports" element={<ReportsAdmin />} />
                {/* <Route path="/stock-securite" element={<StockSecurite />} /> */}
                <Route path="/balance/invoice/:id" element={<BalanceInvoice />} />

              </>
            )}

            {userRole === 2 && (
              userStatus === "actif" ?
                <>
                  <Route path="/" element={<CaissierValidation />} />
                  <Route path="/commande/:reference" element={<CaissierUpdateInvoice />} />
                  <Route path="/commandes/history" element={<CaissierHistory />} />
                  <Route path="/edit-profile" element={<CaissierEditProfile />} />
                </>
                :
                <Route path="/" element={<IsActif />} />
            )}

            {userRole === 4 && (
              userStatus === "actif" ?
                <>
                  <Route path="/" element={<MagasinierValidation />} />
                  <Route path="/livraisons" element={<MagasinierValidation />} />
                  <Route path="/commande/:reference" element={<MagasinierUpdateInvoice />} />
                  <Route path="/commandes/history" element={<MagasinierHistory />} />
                  <Route path="/commandes/validation" element={<OrderValidation />} />
                  <Route path="/summary" element={<MagasinierSummary />} />
                  <Route path="/edit-profile" element={<MagasinierEditProfile />} />
                </>
                :
                <Route path="/" element={<IsActif />} />
            )}

            {userRole === 1 && (
              userStatus === "actif" ?
                <>
                  <Route path="/" element={<Commandes />} />
                  <Route path="/commande/:reference" element={<UpdateInvoice />} />
                  <Route path="/commandes/pros-format" element={<ProFormatInvoice />} />
                  <Route path="/commandes/validation" element={<OrderValidation />} />
                  <Route path="/commandes/history" element={<History />} />
                  <Route path="/edit-profile" element={<EditProfile />} />
                  <Route path="/installment-orders" element={<InstallmentOrderVendeur />} />
                  <Route path="/installment-orders/:id" element={<InstallmentInvoice />} />
                  {/* <Route path="/reports" element={<ReportsUserIndex />} /> */}
                </>
                :
                <Route path="/" element={<IsActif />} />
            )}

            {userRole === 8 && (
              userStatus === "actif" ?
                <>
                  <Route path="/" element={<Comptable />} />
                  <Route path="/commande/:reference" element={<ComptableUpdateInvoice />} />
                  <Route path="/edit-profile" element={<ComptableEditProfile />} />
                  <Route path="/inventory" element={<ComptableInventoryPage />} />
                  <Route path="/sales" element={<ComptableSalesPage />} />
                  <Route path="/rapports" element={<ReportsAdminIndex />} />
                  <Route path="/finance-impots" element={<FinanceImpotsPage />} />
                </>
                :
                <Route path="/" element={<IsActif />} />
            )}


            {userRole === 11 && (
              userStatus === "actif" ?
                <>
                  <Route path="/" element={<ControllerPage />} />
                  <Route path="/orders/:ref" element={<ControllerInvoice />} />
                  <Route path="/commandes/history" element={<ControllerValidatedPage />} />
                  <Route path="/edit-profile" element={<ControllerProfile />} />
                </>
                :
                <Route path="/" element={<IsActif />} />
            )}

            {user?.gestion_custom && user?.role !== 'superAdmin' && renderGrantedRoutes(pageList(user))}

            {isAuthenticated && (
              <Route path="/rapports" element={<ReportsAdminIndex />} />
            )}

            {isAuthenticated && (
              <Route path="/finance-impots" element={<FinanceImpotsPage />} />
            )}

            {isAuthenticated && (
              <Route path="*" element={<NoAccess />} />
            )}
          </>
        )}
      </Route>

      {/* 404 Page */}
      <Route path="*" element={<NotFound404 />} />
    </Routes>
  );
};

export default App;
